'use client';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { BotData } from './useBot';

export interface UpdateBotPayload {
  name?: string;
  slug?: string;
  description?: string;
  status?: 'draft' | 'published' | 'suspended';
  primaryLanguage?: 'en' | 'he';
  visibility?: 'public' | 'unlisted';
  welcomeMessage?: string | null;
  deactivated?: boolean;
  intakeEnabled?: boolean;
  intakeCompletionMessage?: string | null;
  intakePromptMessage?: string | null;
}

interface UpdateBotResponse {
  data: {
    updated: true;
    bot: BotData;
  };
}

async function patchBot(botId: string, updates: UpdateBotPayload): Promise<UpdateBotResponse> {
  const res = await fetch(`/api/bots/${botId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });

  if (!res.ok) {
    const error = await res.json();
    throw new Error(error?.error?.message || 'Failed to update bot');
  }

  return res.json();
}

export function useUpdateBot(botId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (updates: UpdateBotPayload) => patchBot(botId, updates),
    onSuccess: () => {
      // Invalidate both single bot and bots list queries
      queryClient.invalidateQueries({ queryKey: ['bot', botId] });
      queryClient.invalidateQueries({ queryKey: ['bots'] });
    },
  });
}
